import { MOTORES, type Motor } from '@remoto/protocolo';

/**
 * O erro que o MOTOR devolveu — o "por quê" que faltava quando um turno
 * terminava mal.
 *
 * **O defeito que esta forma existe para fechar.** Em 2026-09-21 as sessões do
 * Codex pararam de responder: a mensagem do dono entrava na conversa e nada
 * voltava, sem erro nenhum na tela. A causa não era nossa — a cota da conta
 * acabou —, e o app-server DISSE isso, com mensagem inteira e código próprio:
 *
 *     turn/completed { turn: { status: 'failed', error: {
 *       message: "You've hit your usage limit. Visit …/settings/usage to purchase
 *                 more credits or try again at 11:23 AM.",
 *       codexErrorInfo: 'usageLimitExceeded' } } }
 *
 * O tratador de `turn/completed` lia `durationMs` e `status`, nunca `error`, e
 * a razão morria ali. Sobrava `ehErro: true`, que diz que terminou mal e não
 * diz mais nada. A investigação foi atrás de versão de daemon, de login e de
 * protocolo — horas — porque o painel ficava mudo. Pela régua da casa
 * (`.claude/rules/testes.md`) falha silenciosa é o pior tipo de defeito.
 *
 * **A mensagem viaja PALAVRA POR PALAVRA, e isso é decisão.** Ela já vem
 * pronta, e traz o que fazer e a hora em que a cota volta. Reescrever aqui —
 * ou mapear o código para um texto nosso, como o painel faz com `ErroApi`
 * (`.claude/rules/web.md`) — faria o painel MENTIR no dia em que a OpenAI
 * mudar a razão: um `codexErrorInfo` novo cairia no texto genérico, e a
 * conversa voltaria a não dizer o que houve. O que é nosso é a moldura em
 * pt-BR ao redor; o miolo é do motor.
 *
 * **Esta carga não é prosa, e por isso não é `assistant`.** Um bloco `text`
 * seria indexado por `remoto_texto_do_evento` e o cérebro passaria a achar
 * "You've hit your usage limit" como se o modelo tivesse dito isso. O `tipo`
 * próprio mantém o erro na conversa e fora da busca, sem escrever um segundo
 * extrator de texto.
 */

/** O `tipo` do `EventoSessao`. Nome novo no eixo `tipo`, que é `z.string()`: sem versão de protocolo. */
export const TIPO_ERRO_DO_MOTOR = 'erro-do-motor';

/**
 * Quanto da mensagem do motor viaja.
 *
 * O que se mediu cabe em duas linhas, mas `additionalDetails` existe no
 * binding e ninguém mediu o teto dele. Cortar é melhor que estourar o lote —
 * e o corte NUNCA é silencioso: quem corta marca `truncado: true` no evento,
 * que é o campo que o `EventoSessaoEntrada` já tem para dizer exatamente isso.
 */
export const TETO_MENSAGEM_DO_MOTOR = 2_000;

export interface ErroDoMotor {
  /** O texto do motor, como ele veio. */
  mensagem: string;
  /** O código próprio do motor (`usageLimitExceeded`), quando há um. */
  codigo?: string;
  /** A mensagem não coube inteira em `TETO_MENSAGEM_DO_MOTOR`. */
  cortada?: boolean;
}

function cortar(texto: string): { mensagem: string; cortada: boolean } {
  return texto.length <= TETO_MENSAGEM_DO_MOTOR
    ? { mensagem: texto, cortada: false }
    : { mensagem: texto.slice(0, TETO_MENSAGEM_DO_MOTOR), cortada: true };
}

/**
 * Lê o `error` cru do Codex (`turn.error`, ou o `params.error` da notificação
 * avulsa). **Nunca lança**; devolve `null` quando não há erro nenhum ou quando
 * o que veio não tem texto.
 *
 * Sem mensagem não há evento: um `{codexErrorInfo: 'x'}` sozinho viraria um
 * cartão vermelho dizendo apenas "x", que assusta sem informar. O `ehErro` do
 * fim de turno continua contando que o turno terminou mal.
 */
export function lerErroDoMotor(bruto: unknown): ErroDoMotor | null {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const { message, codexErrorInfo } = bruto as { message?: unknown; codexErrorInfo?: unknown };
  if (typeof message !== 'string') return null;
  const texto = message.trim();
  if (texto === '') return null;
  const { mensagem, cortada } = cortar(texto);
  return {
    mensagem,
    /*
      Só string. O binding declara `codexErrorInfo` como união, e se um dia ela
      chegar como objeto, adivinhar qual campo é "o código" poria na tela uma
      palavra que o motor não disse. Sem código, o cartão mostra a mensagem —
      que é o que a pessoa precisa de qualquer jeito.
    */
    ...(typeof codexErrorInfo === 'string' && codexErrorInfo !== '' ? { codigo: codexErrorInfo } : {}),
    ...(cortada ? { cortada: true } : {}),
  };
}

/** A carga do `EventoSessao`. O `type` interno repete o `tipo` do evento, como todas as outras. */
export function cargaDoErroDoMotor(erro: ErroDoMotor, motor: Motor): Record<string, unknown> {
  return {
    type: TIPO_ERRO_DO_MOTOR,
    motor,
    mensagem: erro.mensagem,
    ...(erro.codigo ? { codigo: erro.codigo } : {}),
    ...(erro.cortada ? { cortada: true } : {}),
  };
}

/**
 * A volta: da carga gravada para o que o painel desenha.
 *
 * Existe para que o front não leia chave crua — e para que a ida e a volta
 * sejam provadas juntas, num teste de ida-e-volta. Duas leituras escritas à
 * mão divergem; foi assim que `assemblyai` sobreviveu meses num enum sem
 * implementação.
 */
export function erroDoMotorDaCarga(carga: unknown): (ErroDoMotor & { motor?: Motor }) | null {
  if (typeof carga !== 'object' || carga === null) return null;
  const { mensagem, codigo, cortada, motor } = carga as {
    mensagem?: unknown;
    codigo?: unknown;
    cortada?: unknown;
    motor?: unknown;
  };
  if (typeof mensagem !== 'string' || mensagem.trim() === '') return null;
  return {
    mensagem,
    ...(typeof codigo === 'string' && codigo !== '' ? { codigo } : {}),
    ...(cortada === true ? { cortada: true } : {}),
    ...(typeof motor === 'string' && (MOTORES as readonly string[]).includes(motor)
      ? { motor: motor as Motor }
      : {}),
  };
}
